import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { api } from '../services/api';
import { theme } from '../theme';
import { LoadingSpinner } from '../components/LoadingSpinner';
import {
  Copy,
  Download,
  MessageCircle,
  Phone,
  Search,
  Sparkles,
  Trash2,
  X,
  UserPlus,
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useCopyContext } from '../context/CopyContext';

export type LeadRow = {
  id: string;
  name: string | null;
  mobile: string | null;
  email: string | null;
  source: string | null;
  budget: string | null;
  location: string | null;
  score: number;
  status: string;
  notes: string | null;
  last_contacted_at: string | null;
  created_at: string;
  updated_at?: string | null;
};

type LeadStats = {
  total: number;
  new: number;
  contacted: number;
  interested: number;
  not_interested: number;
  converted: number;
  qualified?: number;
};

const SOURCE_OPTIONS = [
  'WhatsApp',
  'Facebook',
  'Google',
  'Instagram',
  'Referral',
  'Walk-in',
  'Other',
] as const;

const BUDGET_OPTIONS = ['<5L', '5-10L', '10-25L', '25L+', 'Not mentioned'] as const;

const STATUS_OPTIONS = [
  { db: 'new', label: 'New' },
  { db: 'contacted', label: 'Contacted' },
  { db: 'interested', label: 'Interested' },
  { db: 'not_interested', label: 'Not Interested' },
  { db: 'converted', label: 'Converted' },
] as const;

function toDbSource(label: string): string {
  const m: Record<string, string> = {
    WhatsApp: 'whatsapp',
    Facebook: 'facebook',
    Google: 'google',
    Instagram: 'instagram',
    Referral: 'referral',
    'Walk-in': 'walk_in',
    Other: 'other',
  };
  return m[label] ?? 'other';
}

function fromDbSource(db: string | null): string {
  if (!db) return 'Other';
  const s = db.toLowerCase();
  const map: Record<string, string> = {
    whatsapp: 'WhatsApp',
    facebook: 'Facebook',
    google: 'Google',
    instagram: 'Instagram',
    referral: 'Referral',
    walk_in: 'Walk-in',
    walkin: 'Walk-in',
    website: 'Google',
    organic: 'Other',
    other: 'Other',
  };
  return map[s] || db.charAt(0).toUpperCase() + db.slice(1);
}

function statusLabel(db: string): string {
  const x = db.toLowerCase();
  if (x === 'qualified') return 'Interested';
  const f = STATUS_OPTIONS.find((o) => o.db === x);
  return f?.label || db;
}

function statusDbFromLabel(label: string): string {
  const f = STATUS_OPTIONS.find((o) => o.label === label);
  return f?.db ?? 'new';
}

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function waDigits(mobile: string | null): string {
  if (!mobile) return '';
  let d = mobile.replace(/\D/g, '');
  if (d.length === 10 && !d.startsWith('91')) d = `91${d}`;
  return d;
}

function sourceBadge(db: string | null): { label: string; bg: string; fg: string } {
  const key = (db || '').toLowerCase();
  const map: Record<string, { label: string; bg: string; fg: string }> = {
    whatsapp: { label: 'WhatsApp', bg: 'rgba(16,185,129,0.2)', fg: '#34D399' },
    facebook: { label: 'Facebook', bg: 'rgba(59,130,246,0.2)', fg: '#60A5FA' },
    google: { label: 'Google', bg: 'rgba(234,179,8,0.2)', fg: '#FBBF24' },
    instagram: { label: 'Instagram', bg: 'rgba(236,72,153,0.2)', fg: '#F472B6' },
    referral: { label: 'Referral', bg: 'rgba(168,85,247,0.2)', fg: '#C084FC' },
    walk_in: { label: 'Walk-in', bg: 'rgba(6,148,148,0.2)', fg: '#069494' },
    walkin: { label: 'Walk-in', bg: 'rgba(6,148,148,0.2)', fg: '#069494' },
  };
  return map[key] || { label: fromDbSource(db), bg: 'rgba(107,114,128,0.25)', fg: '#9CA3AF' };
}

function statusBadgeClass(db: string): string {
  const s = db.toLowerCase();
  if (s === 'new') return 'odc-badge-cyan';
  if (s === 'contacted') return 'odc-badge-amber';
  if (s === 'interested' || s === 'qualified') return 'odc-badge-accent';
  if (s === 'not_interested' || s === 'lost') return 'odc-badge-cyan';
  if (s === 'converted') return 'odc-badge-green';
  return 'odc-badge-cyan';
}

function scoreColor(score: number): string {
  if (score >= 70) return theme.success;
  if (score >= 40) return theme.warning;
  return theme.danger;
}

function exportLeadsCsv(rows: LeadRow[]) {
  const headers = [
    'id',
    'name',
    'mobile',
    'email',
    'source',
    'budget',
    'location',
    'notes',
    'status',
    'score',
    'created_at',
  ];
  const esc = (v: string | number | null | undefined) => {
    const s = v == null ? '' : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [
    headers.join(','),
    ...rows.map((l) =>
      headers
        .map((h) => esc((l as unknown as Record<string, unknown>)[h] as string | number | null))
        .join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `odc-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const emptyAddForm = {
  name: '',
  mobile: '',
  email: '',
  source: 'WhatsApp' as (typeof SOURCE_OPTIONS)[number],
  budget: 'Not mentioned' as (typeof BUDGET_OPTIONS)[number],
  location: '',
  notes: '',
  status: 'New',
};

export function LeadsPage() {
  const { showToast } = useToast();
  const { copyText } = useCopyContext();
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [selectedLead, setSelectedLead] = useState<LeadRow | null>(null);
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [saving, setSaving] = useState(false);
  const [aiBusyId, setAiBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const params: Record<string, string> = {};
    if (filterStatus !== 'all') params.status = filterStatus;
    if (filterSource !== 'all') params.source = filterSource;
    const [lr, st] = await Promise.all([
      api.get<{ leads: LeadRow[] }>('/leads', { params }),
      api.get<{ stats: LeadStats }>('/leads/stats'),
    ]);
    setLeads(lr.data.leads ?? []);
    setStats(st.data.stats ?? null);
  }, [filterStatus, filterSource]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        await load();
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const msg = e.response?.data?.error || e.message || 'Could not load leads.';
          setError(msg);
          showToast(msg, 'error');
        } else {
          setError('Could not load leads.');
          showToast('Could not load leads.', 'error');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [load, showToast]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showAddModal) {
        e.preventDefault();
        setShowAddModal(false);
        return;
      }
      if (selectedLead) {
        e.preventDefault();
        setSelectedLead(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAddModal, selectedLead]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      const blob = `${l.name || ''} ${l.mobile || ''} ${l.email || ''} ${l.location || ''} ${l.notes || ''} ${l.source || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [leads, search]);

  async function addLead() {
    if (!addForm.name.trim() || !addForm.mobile.trim()) {
      setError('Name and mobile are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post('/leads', {
        name: addForm.name.trim(),
        mobile: addForm.mobile.trim(),
        email: addForm.email.trim() || undefined,
        source: toDbSource(addForm.source),
        budget: addForm.budget,
        location: addForm.location.trim() || undefined,
        notes: addForm.notes.trim() || undefined,
        status: statusDbFromLabel(addForm.status),
      });
      setShowAddModal(false);
      setAddForm(emptyAddForm);
      await load();
      showToast('Lead save ho gaya — ab WhatsApp ya call se connect karein.', 'success');
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const msg = (e.response?.data as { error?: string })?.error || e.message || 'Could not add lead.';
        setError(msg);
        showToast(msg, 'error');
      } else {
        setError('Could not add lead.');
        showToast('Could not add lead.', 'error');
      }
    } finally {
      setSaving(false);
    }
  }

  async function updateLeadStatus(id: string, status: string) {
    setError(null);
    try {
      await api.put(`/leads/${id}`, { status });
      await load();
      showToast('Status update ho gaya.', 'success');
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const msg = (e.response?.data as { error?: string })?.error || 'Could not update status.';
        setError(msg);
        showToast(msg, 'error');
      }
    }
  }

  async function deleteLead(id: string) {
    if (!window.confirm('Delete this lead permanently?')) return;
    setError(null);
    try {
      await api.delete(`/leads/${id}`);
      setSelectedLead(null);
      await load();
      showToast('Lead delete ho gaya.', 'success');
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const msg = (e.response?.data as { error?: string })?.error || 'Could not delete lead.';
        setError(msg);
        showToast(msg, 'error');
      }
    }
  }

  async function scoreWithAi(lead: LeadRow) {
    setAiBusyId(lead.id);
    setError(null);
    try {
      const { data } = await api.post<{ lead_score?: { score?: number } }>('/agent-ops/lead-score', {
        leadName: lead.name || 'Lead',
        mobile: lead.mobile || '',
        source: fromDbSource(lead.source),
        message: lead.notes || '',
        budget: lead.budget || 'Not mentioned',
        location: lead.location || '',
        interest: lead.notes || '',
      });
      const raw = data.lead_score as { score?: number } | undefined;
      const score = typeof raw?.score === 'number' ? Math.min(100, Math.max(0, raw.score)) : undefined;
      if (score == null) {
        const msg = 'AI se score clear nahi aaya — dubara try karein.';
        setError(msg);
        showToast(msg, 'error');
        return;
      }
      await api.put(`/leads/${lead.id}`, { score });
      await load();
      showToast(`AI score save: ${score}/100`, 'success');
    } catch {
      const msg = 'Lead scoring fail — API / network check karein.';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setAiBusyId(null);
    }
  }

  function openWhatsApp(lead: LeadRow) {
    const d = waDigits(lead.mobile);
    if (!d) return;
    const name = lead.name?.trim() || 'there';
    const text = encodeURIComponent(
      `Hi ${name}, this is ${document.title.includes('ODC') ? 'ODC' : 'our team'}. Thanks for your interest — how can we help you today?`
    );
    window.open(`https://wa.me/${d}?text=${text}`, '_blank', 'noopener,noreferrer');
  }

  if (loading) return <LoadingSpinner />;

  const statTiles = stats
    ? [
        { k: 'Total', v: stats.total, c: theme.accent },
        { k: 'New', v: stats.new, c: theme.primary },
        { k: 'Contacted', v: stats.contacted, c: theme.warning },
        { k: 'Interested', v: stats.interested, c: theme.secondary },
        { k: 'Not interested', v: stats.not_interested, c: theme.danger },
        { k: 'Converted', v: stats.converted, c: theme.success },
      ]
    : [];

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="min-w-0">
          <div
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1"
            style={{ borderColor: 'var(--border2)', background: 'var(--card)' }}
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.accent }}>
              CRM
            </span>
          </div>
          <h1 className="odc-page-title mt-3 text-2xl font-bold md:text-3xl" style={{ color: 'var(--text)' }}>
            Leads
          </h1>
          <p className="mt-1 max-w-xl text-sm" style={{ color: 'var(--muted)' }}>
            Full pipeline: capture, score, WhatsApp, CSV export — mobile & desktop. Tip:{' '}
            <kbd className="rounded border px-1 text-[10px]" style={{ borderColor: 'var(--border)' }}>
              ⌘K
            </kbd>{' '}
            /{' '}
            <kbd className="rounded border px-1 text-[10px]" style={{ borderColor: 'var(--border)' }}>
              Ctrl+K
            </kbd>{' '}
            se kahi bhi jump.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="odc-btn-ghost flex items-center gap-2 !py-2.5 text-sm"
            onClick={() => {
              exportLeadsCsv(filtered);
              showToast(`${filtered.length} leads ka CSV download shuru.`, 'success');
            }}
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
          <button
            type="button"
            className="odc-btn-primary flex items-center gap-2 !py-2.5 text-sm"
            onClick={() => {
              setAddForm(emptyAddForm);
              setShowAddModal(true);
            }}
          >
            <UserPlus className="h-4 w-4" />
            Add lead
          </button>
        </div>
      </header>

      {error ? (
        <div
          className="odc-card !rounded-xl !p-4 text-sm font-semibold"
          style={{ transform: 'none', borderColor: 'rgba(183,65,14,0.45)', color: theme.danger }}
          role="alert"
        >
          {error}
          <button type="button" className="ml-3 underline" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {statTiles.length > 0 ? (
        <div className="odc-stat-cards grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {statTiles.map((s, i) => (
            <div
              key={s.k}
              className="odc-stat-card odc-stagger-1"
              style={{ ['--stat-color' as string]: s.c, animationDelay: `${i * 0.04}s` }}
            >
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                {s.k}
              </p>
              <p className="stat-value mt-1 text-2xl font-black" style={{ color: 'var(--text)' }}>
                {s.v}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="odc-card !p-4 sm:!p-5" style={{ transform: 'none' }}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
              Status
            </p>
            <div className="flex flex-wrap gap-2">
              {['all', 'new', 'contacted', 'interested', 'not_interested', 'converted'].map((s) => {
                const active = filterStatus === s;
                const label =
                  s === 'all'
                    ? 'All'
                    : s === 'not_interested'
                      ? 'Not interested'
                      : s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ');
                return (
                  <button
                    key={s}
                    type="button"
                    className={`odc-pill-tab ${active ? 'active' : ''}`}
                    style={
                      active
                        ? {
                            borderColor: theme.primary,
                            background: `${theme.primary}22`,
                            color: 'var(--text)',
                          }
                        : undefined
                    }
                    onClick={() => setFilterStatus(s)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="pt-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
              Source
            </p>
            <div className="flex flex-wrap gap-2">
              {['all', 'whatsapp', 'facebook', 'google', 'instagram', 'referral', 'walk_in', 'other'].map((s) => {
                const active = filterSource === s;
                const label = s === 'all' ? 'All sources' : fromDbSource(s);
                return (
                  <button
                    key={s}
                    type="button"
                    className={`odc-pill-tab text-xs ${active ? 'active' : ''}`}
                    style={
                      active
                        ? {
                            borderColor: theme.accent,
                            background: 'rgba(6,148,148,0.15)',
                            color: 'var(--text)',
                          }
                        : undefined
                    }
                    onClick={() => setFilterSource(s)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="relative w-full max-w-md flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
              style={{ color: 'var(--muted)' }}
            />
            <input
              className="odc-input !pl-10 text-sm"
              placeholder="Search name, phone, email, location, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search leads"
            />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="odc-card !p-12 text-center" style={{ transform: 'none' }}>
          <p className="text-lg font-bold" style={{ color: 'var(--text)' }}>
            No leads yet
          </p>
          <p className="mx-auto mt-2 max-w-md text-sm" style={{ color: 'var(--muted)' }}>
            Add your first lead to start tracking conversations, WhatsApp follow-ups, and AI scores in one place.
          </p>
          <button
            type="button"
            className="odc-btn-primary mx-auto mt-6 inline-flex items-center gap-2 !py-3"
            onClick={() => {
              setAddForm(emptyAddForm);
              setShowAddModal(true);
            }}
          >
            <UserPlus className="h-4 w-4" />
            Add your first lead
          </button>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="odc-leads-cards flex flex-col gap-4 md:hidden">
            {filtered.map((l) => {
              const src = sourceBadge(l.source);
              const sc = scoreColor(l.score);
              return (
                <div key={l.id} className="odc-card !p-4" style={{ transform: 'none' }}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-bold" style={{ color: 'var(--text)' }}>
                        {l.name || 'Unnamed'}
                      </p>
                      <p className="mt-0.5 font-mono text-xs" style={{ color: 'var(--muted)' }}>
                        {l.mobile || '—'}
                      </p>
                    </div>
                    <span className={`odc-badge shrink-0 capitalize ${statusBadgeClass(l.status)}`}>
                      {statusLabel(l.status)}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-md px-2 py-0.5 text-[10px] font-bold" style={{ background: src.bg, color: src.fg }}>
                      {src.label}
                    </span>
                    {l.budget ? (
                      <span className="rounded-md border px-2 py-0.5 text-[10px] font-bold" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
                        {l.budget}
                      </span>
                    ) : null}
                  </div>
                  {l.location ? (
                    <p className="mt-2 text-xs" style={{ color: 'var(--muted)' }}>
                      📍 {l.location}
                    </p>
                  ) : null}
                  <div className="mt-3 flex items-center gap-3">
                    <div
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-xs font-black text-white"
                      style={{
                        background: `conic-gradient(${sc} ${Math.min(100, l.score) * 3.6}deg, var(--card2) 0deg)`,
                      }}
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: 'var(--bg2)' }}>
                        <span style={{ color: sc }}>{l.score}</span>
                      </span>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      {timeAgo(l.created_at)}
                    </p>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                    {l.mobile ? (
                      <a
                        href={`tel:${l.mobile.replace(/\D/g, '')}`}
                        className="odc-btn-ghost inline-flex !py-2 text-xs"
                        aria-label="Call"
                      >
                        <Phone className="h-4 w-4" />
                      </a>
                    ) : null}
                    {l.mobile ? (
                      <button
                        type="button"
                        className="odc-btn-ghost inline-flex !py-2 text-xs"
                        title="Copy mobile"
                        aria-label="Copy mobile"
                        onClick={() => copyText(l.mobile!.replace(/\s/g, ''), 'Mobile copy ho gaya')}
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    ) : null}
                    <button type="button" className="odc-btn-ghost inline-flex !py-2 text-xs" onClick={() => openWhatsApp(l)}>
                      <MessageCircle className="h-4 w-4" style={{ color: theme.success }} />
                    </button>
                    <button
                      type="button"
                      className="odc-btn-ghost inline-flex !py-2 text-xs"
                      disabled={aiBusyId === l.id}
                      onClick={() => void scoreWithAi(l)}
                    >
                      <Sparkles className="h-4 w-4" style={{ color: theme.warning }} />
                    </button>
                    <button type="button" className="odc-btn-ghost inline-flex !py-2 text-xs" onClick={() => setSelectedLead(l)}>
                      Details
                    </button>
                    <button
                      type="button"
                      className="odc-btn-ghost ml-auto inline-flex !py-2 text-xs"
                      style={{ color: theme.danger }}
                      onClick={() => void deleteLead(l.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="odc-card odc-table-scroll hidden overflow-x-auto !rounded-2xl !p-0 md:block" style={{ transform: 'none' }}>
            <table className="odc-table min-w-[960px] text-sm">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Source</th>
                  <th>Budget</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => {
                  const src = sourceBadge(l.source);
                  const sc = scoreColor(l.score);
                  return (
                    <tr key={l.id}>
                      <td>
                        <div>
                          <p className="font-semibold" style={{ color: 'var(--text)' }}>
                            {l.name || 'Unnamed'}
                          </p>
                          <p className="font-mono text-xs" style={{ color: 'var(--muted)' }}>
                            {l.mobile || '—'}
                          </p>
                          {l.email ? (
                            <p className="text-xs" style={{ color: 'var(--muted)' }}>
                              {l.email}
                            </p>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span className="rounded-md px-2 py-0.5 text-[10px] font-bold" style={{ background: src.bg, color: src.fg }}>
                          {src.label}
                        </span>
                      </td>
                      <td className="text-xs" style={{ color: 'var(--muted)' }}>
                        {l.budget || '—'}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[10px] font-black text-white"
                            style={{
                              background: `conic-gradient(${sc} ${Math.min(100, l.score) * 3.6}deg, var(--card2) 0deg)`,
                            }}
                          >
                            <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: 'var(--bg2)' }}>
                              <span style={{ color: sc }}>{l.score}</span>
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <select
                          className="odc-select !max-w-[160px] !py-2 text-xs capitalize"
                          value={l.status.toLowerCase() === 'qualified' ? 'interested' : l.status.toLowerCase()}
                          onChange={(e) => void updateLeadStatus(l.id, e.target.value)}
                          aria-label={`Status for ${l.name}`}
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.db} value={o.db}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="text-xs" style={{ color: 'var(--muted)' }}>
                        {timeAgo(l.updated_at || l.created_at)}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {l.mobile ? (
                            <a
                              href={`tel:${l.mobile.replace(/\D/g, '')}`}
                              className="rounded-lg border p-2 transition hover:opacity-90"
                              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                              title="Call"
                            >
                              <Phone className="h-4 w-4" />
                            </a>
                          ) : null}
                          {l.mobile ? (
                            <button
                              type="button"
                              className="rounded-lg border p-2 transition hover:opacity-90"
                              style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
                              title="Copy mobile"
                              onClick={() => copyText(l.mobile!.replace(/\s/g, ''), 'Mobile copy ho gaya')}
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="rounded-lg border p-2 transition hover:opacity-90"
                            style={{ borderColor: 'var(--border)', color: theme.success }}
                            title="WhatsApp"
                            onClick={() => openWhatsApp(l)}
                          >
                            <MessageCircle className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded-lg border p-2 transition hover:opacity-90 disabled:opacity-40"
                            style={{ borderColor: 'var(--border)', color: theme.warning }}
                            title="Score with AI"
                            disabled={aiBusyId === l.id}
                            onClick={() => void scoreWithAi(l)}
                          >
                            <Sparkles className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded-lg border p-2 transition hover:opacity-90"
                            style={{ borderColor: 'var(--border)', color: theme.primary }}
                            title="Details"
                            onClick={() => setSelectedLead(l)}
                          >
                            👁
                          </button>
                          <button
                            type="button"
                            className="rounded-lg border p-2 transition hover:opacity-90"
                            style={{ borderColor: 'var(--border)', color: theme.danger }}
                            title="Delete"
                            onClick={() => void deleteLead(l.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Detail sheet */}
      <div
        className={`fixed inset-0 z-50 transition ${selectedLead ? 'pointer-events-auto' : 'pointer-events-none'}`}
        style={{ background: selectedLead ? 'rgba(0,0,0,0.55)' : 'transparent' }}
        onClick={() => setSelectedLead(null)}
        aria-hidden={!selectedLead}
      >
        <div
          className="absolute right-0 top-0 h-full w-full max-w-md border-l shadow-2xl transition-transform duration-300"
          style={{
            background: 'var(--bg2)',
            borderColor: 'var(--border)',
            transform: selectedLead ? 'translateX(0)' : 'translateX(100%)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {selectedLead ? (
            <div className="flex h-full flex-col overflow-y-auto p-6">
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-xl font-bold" style={{ color: 'var(--text)' }}>
                  {selectedLead.name || 'Lead'}
                </h2>
                <button
                  type="button"
                  className="rounded-lg p-1 transition hover:bg-[var(--card)]"
                  style={{ color: 'var(--muted)' }}
                  onClick={() => setSelectedLead(null)}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <dl className="mt-6 space-y-4 text-sm">
                <div>
                  <dt style={{ color: 'var(--muted)' }}>Mobile</dt>
                  <dd className="flex flex-wrap items-center gap-2 font-mono" style={{ color: 'var(--text)' }}>
                    <span>{selectedLead.mobile || '—'}</span>
                    {selectedLead.mobile ? (
                      <button
                        type="button"
                        className="odc-btn-ghost !py-1 !text-xs"
                        onClick={() =>
                          copyText(selectedLead.mobile!.replace(/\s/g, ''), 'Mobile copy ho gaya')
                        }
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt style={{ color: 'var(--muted)' }}>Email</dt>
                  <dd style={{ color: 'var(--text)' }}>{selectedLead.email || '—'}</dd>
                </div>
                <div>
                  <dt style={{ color: 'var(--muted)' }}>Source</dt>
                  <dd>
                    <span
                      className="rounded-md px-2 py-0.5 text-[10px] font-bold"
                      style={{
                        background: sourceBadge(selectedLead.source).bg,
                        color: sourceBadge(selectedLead.source).fg,
                      }}
                    >
                      {sourceBadge(selectedLead.source).label}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt style={{ color: 'var(--muted)' }}>Budget</dt>
                  <dd style={{ color: 'var(--text)' }}>{selectedLead.budget || '—'}</dd>
                </div>
                <div>
                  <dt style={{ color: 'var(--muted)' }}>Location</dt>
                  <dd style={{ color: 'var(--text)' }}>{selectedLead.location || '—'}</dd>
                </div>
                <div>
                  <dt style={{ color: 'var(--muted)' }}>Status</dt>
                  <dd>
                    <select
                      className="odc-select mt-1 w-full !py-2 text-sm capitalize"
                      value={
                        selectedLead.status.toLowerCase() === 'qualified' ? 'interested' : selectedLead.status.toLowerCase()
                      }
                      onChange={(e) => void updateLeadStatus(selectedLead.id, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.db} value={o.db}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </dd>
                </div>
                <div>
                  <dt style={{ color: 'var(--muted)' }}>Score</dt>
                  <dd className="text-2xl font-black" style={{ color: scoreColor(selectedLead.score) }}>
                    {selectedLead.score}
                  </dd>
                </div>
                <div>
                  <dt style={{ color: 'var(--muted)' }}>Notes</dt>
                  <dd style={{ color: 'var(--muted)' }}>{selectedLead.notes || '—'}</dd>
                </div>
                <div>
                  <dt style={{ color: 'var(--muted)' }}>Created</dt>
                  <dd style={{ color: 'var(--muted)' }}>{timeAgo(selectedLead.created_at)}</dd>
                </div>
              </dl>
              <div className="mt-auto flex flex-col gap-2 border-t pt-6" style={{ borderColor: 'var(--border)' }}>
                <button type="button" className="odc-btn-primary w-full justify-center !py-2.5" onClick={() => openWhatsApp(selectedLead)}>
                  WhatsApp quick message
                </button>
                <button
                  type="button"
                  className="odc-btn-ghost w-full justify-center !py-2.5"
                  disabled={aiBusyId === selectedLead.id}
                  onClick={() => void scoreWithAi(selectedLead)}
                >
                  {aiBusyId === selectedLead.id ? 'Scoring…' : 'Score with AI'}
                </button>
                <button
                  type="button"
                  className="odc-btn-ghost w-full justify-center !py-2.5"
                  style={{ color: theme.danger }}
                  onClick={() => void deleteLead(selectedLead.id)}
                >
                  Delete lead
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showAddModal ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={() => !saving && setShowAddModal(false)}
        >
          <div className="odc-card max-h-[90vh] w-full max-w-lg overflow-y-auto !p-6" style={{ transform: 'none' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
              Add lead
            </h3>
            <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
              Name and mobile are required. Everything else helps AI scoring and follow-up.
            </p>
            <div className="mt-5 space-y-4">
              <label className="block text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                Name *
                <input
                  className="odc-input mt-1 w-full !py-2.5 text-sm"
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label className="block text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                Mobile *
                <input
                  className="odc-input mt-1 w-full !py-2.5 text-sm"
                  value={addForm.mobile}
                  onChange={(e) => setAddForm((f) => ({ ...f, mobile: e.target.value }))}
                />
              </label>
              <label className="block text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                Email
                <input
                  className="odc-input mt-1 w-full !py-2.5 text-sm"
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                />
              </label>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  Source
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {SOURCE_OPTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="odc-pill-tab text-xs"
                      style={
                        addForm.source === s
                          ? { borderColor: theme.primary, background: `${theme.primary}22`, color: 'var(--text)' }
                          : undefined
                      }
                      onClick={() => setAddForm((f) => ({ ...f, source: s }))}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  Budget
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {BUDGET_OPTIONS.map((b) => (
                    <button
                      key={b}
                      type="button"
                      className="odc-pill-tab text-xs"
                      style={
                        addForm.budget === b
                          ? { borderColor: theme.accent, background: 'rgba(6,148,148,0.15)', color: 'var(--text)' }
                          : undefined
                      }
                      onClick={() => setAddForm((f) => ({ ...f, budget: b }))}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <label className="block text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                Location
                <input
                  className="odc-input mt-1 w-full !py-2.5 text-sm"
                  value={addForm.location}
                  onChange={(e) => setAddForm((f) => ({ ...f, location: e.target.value }))}
                />
              </label>
              <label className="block text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                Interest / notes
                <textarea
                  className="odc-input mt-1 min-h-[5rem] w-full resize-y text-sm"
                  value={addForm.notes}
                  onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </label>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  Status
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((o) => (
                    <button
                      key={o.db}
                      type="button"
                      className="odc-pill-tab text-xs capitalize"
                      style={
                        addForm.status === o.label
                          ? { borderColor: theme.warning, background: 'rgba(255,206,27,0.12)', color: 'var(--text)' }
                          : undefined
                      }
                      onClick={() => setAddForm((f) => ({ ...f, status: o.label }))}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap justify-end gap-2">
              <button type="button" className="odc-btn-ghost !py-2.5 text-sm" disabled={saving} onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button type="button" className="odc-btn-primary !py-2.5 text-sm" disabled={saving} onClick={() => void addLead()}>
                {saving ? 'Saving…' : 'Save lead'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
